import { z } from 'zod';
/**
 * Auth request shapes.
 *
 * Normalisation happens HERE, in the schema, not in the service - so the SPA,
 * a future MCP tool and the invite-acceptance flow all get identical treatment
 * without three copies of `.trim().toLowerCase()`. An email that differs only
 * by case must not become a second account.
 */
export const emailSchema = z.string().trim().toLowerCase().email('Enter a valid email address');
/**
 * Minimum 10 characters, and that is the only rule.
 *
 * Composition rules (one uppercase, one symbol, …) measurably push people
 * toward `Password1!` and a sticky note. Length is the property that actually
 * costs an attacker something, so it is the property enforced. The upper bound
 * exists because bcrypt silently truncates past 72 BYTES - a limit a user
 * pasting a passphrase can genuinely reach, and one that would otherwise mean
 * two different passwords open the same account.
 *
 * TextEncoder rather than Buffer.byteLength: this package is imported by the
 * browser as well as the API, and Buffer does not exist there. The check would
 * have thrown on the register form rather than validating anything.
 */
export const passwordSchema = z
    .string()
    .min(10, 'Use at least 10 characters - length matters more than symbols')
    .refine((v) => new TextEncoder().encode(v).length <= 72, {
    message: 'That password is too long. Use 72 bytes or fewer.',
});
export const registerSchema = z
    .object({
    name: z.string().trim().min(1, 'Tell us your name').max(120),
    email: emailSchema,
    password: passwordSchema,
    /** Optional: creates the org in the same transaction as the user. */
    orgName: z.string().trim().min(1).max(160).optional(),
})
    .strict();
export const loginSchema = z
    .object({
    email: emailSchema,
    // Deliberately NOT passwordSchema. Validating the length of a login attempt
    // tells an attacker the rule and rejects legacy passwords that predate it;
    // a wrong password is a wrong password.
    password: z.string().min(1, 'Enter your password'),
})
    .strict();
export const mfaVerifySchema = z
    .object({ code: z.string().trim().regex(/^\d{6}$/, 'Enter the six-digit code') })
    .strict();
export const requestPasswordResetSchema = z.object({ email: emailSchema }).strict();
export const resetPasswordSchema = z
    .object({
    email: emailSchema,
    otp: z.string().trim().regex(/^\d{6}$/, 'Enter the six-digit code'),
    password: passwordSchema,
})
    .strict();
export const changePasswordSchema = z
    .object({ currentPassword: z.string().min(1), password: passwordSchema })
    .strict();
export const updateProfileSchema = z
    .object({ name: z.string().trim().min(1).max(120).optional() })
    .strict();
//# sourceMappingURL=auth.js.map